import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ChevronDown,
  FileText,
  Folder,
  Loader2,
  AlertTriangle,
} from "lucide-react"

import { Button, Label } from "@f2e/ui/components/base"
import { useToast } from "@/hooks"
import {
  FetchStatus,
  useBatchKnowledgeBaseNodesApi,
  useListKnowledgeBaseNodesApi,
} from "@/hooks/api"
import type { KnowledgeNodeSchema } from "@/hooks/api"
import {
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
} from "@/lib/apiErrors"

import type { UnifiedTool } from "./type"
import { InvocationInstructionField } from "./InvocationInstructionField"

export interface KnowledgeBaseToolSectionProps {
  tool: UnifiedTool
  disabled?: boolean
  organizationId?: string
  workspaceId?: string
  onUpdateTool: (toolId: string, updates: Partial<UnifiedTool>) => void
  onInvalidSelectionChange?: (toolId: string, hasInvalid: boolean) => void
  showInvocationInstruction?: boolean
}

export const KnowledgeBaseToolSection = ({
  tool,
  disabled = false,
  organizationId,
  workspaceId,
  onUpdateTool,
  onInvalidSelectionChange,
  showInvocationInstruction = true,
}: KnowledgeBaseToolSectionProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set())
  const [selectingFolderIds, setSelectingFolderIds] = useState<Set<string>>(
    new Set()
  )
  const [childrenByParentId, setChildrenByParentId] = useState<
    Record<string, KnowledgeNodeSchema[] | undefined>
  >({})
  const [childrenLoadErrors, setChildrenLoadErrors] = useState<
    Record<string, unknown>
  >({})
  const activeRequests = React.useRef<Record<string, boolean>>({})
  const {
    data: rootsResp,
    error: rootsError,
    status: rootsStatus,
    fetchFn: listRootNodes,
  } = useListKnowledgeBaseNodesApi()
  const { fetchFn: listChildNodes } = useListKnowledgeBaseNodesApi()
  const { fetchFn: listSelectedNodes } = useBatchKnowledgeBaseNodesApi()
  const listSelectedNodesRef = React.useRef(listSelectedNodes)
  useEffect(() => {
    listSelectedNodesRef.current = listSelectedNodes
  }, [listSelectedNodes])
  const [selectedNodeMap, setSelectedNodeMap] = useState<
    Record<string, KnowledgeNodeSchema>
  >({})
  const [missingSelectedIds, setMissingSelectedIds] = useState<Set<string>>(
    new Set()
  )
  const [selectedNodesResolveFailed, setSelectedNodesResolveFailed] =
    useState(false)
  const [selectedNodesResolveError, setSelectedNodesResolveError] =
    useState<unknown>()
  const selectedNodeIds = useMemo(
    () => tool.knowledgeNodeIds ?? [],
    [tool.knowledgeNodeIds]
  )
  const selectedNodeIdsKey = useMemo(
    () => Array.from(new Set(selectedNodeIds)).sort().join(","),
    [selectedNodeIds]
  )
  const selectedNodeIdsRef = React.useRef(selectedNodeIds)
  const selectedNodesRequestIdRef = React.useRef(0)
  useEffect(() => {
    selectedNodeIdsRef.current = selectedNodeIds
  }, [selectedNodeIds])
  const selectedFileIds = new Set(selectedNodeIds)

  const updateKnowledgeNodeSelection = (
    updater: (currentIds: string[]) => string[]
  ) => {
    const nextIds = Array.from(new Set(updater(selectedNodeIdsRef.current)))
    selectedNodeIdsRef.current = nextIds
    selectedNodesRequestIdRef.current += 1
    onUpdateTool(tool.id, { knowledgeNodeIds: nextIds })
  }

  useEffect(() => {
    if (!organizationId || !workspaceId) return
    setChildrenByParentId({})
    setChildrenLoadErrors({})
    activeRequests.current = {}
    listRootNodes({
      urlBuilderParams: {
        organizationId,
        workspaceId,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, workspaceId])

  useEffect(() => {
    const requestId = ++selectedNodesRequestIdRef.current
    if (!organizationId || !workspaceId) {
      setSelectedNodeMap({})
      setMissingSelectedIds(new Set())
      setSelectedNodesResolveFailed(false)
      setSelectedNodesResolveError(undefined)
      return
    }

    const selectedIds = selectedNodeIdsKey ? selectedNodeIdsKey.split(",") : []

    if (selectedIds.length === 0) {
      setSelectedNodeMap({})
      setMissingSelectedIds(new Set())
      setSelectedNodesResolveFailed(false)
      setSelectedNodesResolveError(undefined)
      return
    }

    setSelectedNodesResolveFailed(false)
    setSelectedNodesResolveError(undefined)
    listSelectedNodesRef
      .current({
        urlBuilderParams: {
          organizationId,
          workspaceId,
        },
        jsonBody: {
          ids: selectedIds,
          include_deleted: true,
          include_ancestors: true,
        },
      })
      .then((response) => {
        if (requestId !== selectedNodesRequestIdRef.current) return
        const nodes = response?.data ?? []
        const nextMap: Record<string, KnowledgeNodeSchema> = {}
        nodes.forEach((node) => {
          nextMap[String(node.id)] = node
        })
        setSelectedNodeMap(nextMap)
        setMissingSelectedIds(new Set(selectedIds.filter((id) => !nextMap[id])))
      })
      .catch((error: unknown) => {
        if (requestId !== selectedNodesRequestIdRef.current) return
        console.error("Failed to resolve selected knowledge nodes", error)
        setSelectedNodesResolveError(error)
        setSelectedNodesResolveFailed(true)
      })
    return () => {
      if (requestId === selectedNodesRequestIdRef.current) {
        selectedNodesRequestIdRef.current += 1
      }
    }
  }, [organizationId, selectedNodeIdsKey, workspaceId])

  useEffect(() => {
    if (!rootsError) return
    console.error("Failed to list knowledge base roots", rootsError)
  }, [rootsError])

  const ensureChildrenLoaded = (parentId: string) => {
    if (!organizationId || !workspaceId) return
    if (childrenByParentId[parentId] !== undefined) return
    if (activeRequests.current[parentId]) return

    activeRequests.current[parentId] = true
    setChildrenByParentId((prev) => ({ ...prev, [parentId]: undefined }))
    setChildrenLoadErrors((prev) => {
      const next = { ...prev }
      delete next[parentId]
      return next
    })

    listChildNodes({
      urlBuilderParams: {
        organizationId,
        workspaceId,
      },
      searchParams: new URLSearchParams({
        parent_id: parentId,
      }),
    })
      .then((resp: { data?: KnowledgeNodeSchema[] } | undefined) => {
        setChildrenByParentId((prev) => ({
          ...prev,
          [parentId]: resp?.data ?? [],
        }))
      })
      .catch((err: unknown) => {
        console.error("Failed to list knowledge node children", err)
        setChildrenLoadErrors((prev) => ({ ...prev, [parentId]: err }))
      })
      .finally(() => {
        delete activeRequests.current[parentId]
      })
  }

  const toggleNodeExpanded = (nodeId: string) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) {
        next.delete(nodeId)
      } else {
        next.add(nodeId)
        ensureChildrenLoaded(nodeId)
      }
      return next
    })
  }

  const isSelectableFile = (node: KnowledgeNodeSchema) =>
    node.node_type === "FILE" &&
    !node.deleted_at &&
    ((node.status === "READY" && !!node.snapshot_object_id) ||
      node.status === "PENDING" ||
      node.status === "INDEXING")

  const getVisibleChildren = (children?: KnowledgeNodeSchema[]) =>
    (children ?? []).filter((node) => {
      if (node.node_type === "FOLDER") return true
      if (node.node_type === "FILE" && !node.deleted_at) return true
      if (node.deleted_at && selectedFileIds.has(String(node.id))) return true
      return false
    })

  const mergeChildrenWithSelectedDeleted = (
    parentId: string,
    children?: KnowledgeNodeSchema[]
  ) => {
    const baseChildren = children ?? []
    const deletedSelected = Object.values(selectedNodeMap).filter((node) => {
      if (String(node.parent_id ?? "") !== parentId) return false
      if (!node.deleted_at) return false
      if (node.node_type === "FOLDER") {
        return deletedAncestorIds.has(String(node.id))
      }
      return selectedFileIds.has(String(node.id))
    })
    if (deletedSelected.length === 0) return baseChildren
    const mergedMap = new Map<string, KnowledgeNodeSchema>()
    baseChildren.forEach((node) => mergedMap.set(String(node.id), node))
    deletedSelected.forEach((node) => mergedMap.set(String(node.id), node))
    return Array.from(mergedMap.values())
  }

  const collectDescendantFileIdsFromCache = (
    nodeId: string,
    acc: Set<string>
  ) => {
    const children = childrenByParentId[nodeId]
    if (!children) return
    const mergedChildren = mergeChildrenWithSelectedDeleted(nodeId, children)
    for (const child of mergedChildren) {
      if (child.node_type === "FOLDER") {
        collectDescendantFileIdsFromCache(String(child.id), acc)
      } else if (
        isSelectableFile(child) ||
        (child.deleted_at && selectedFileIds.has(String(child.id)))
      ) {
        acc.add(String(child.id))
      }
    }
  }

  const hasDeletedSelectedInSubtree = (nodeId: string): boolean => {
    if (deletedAncestorIds.has(nodeId)) return true
    const children = childrenByParentId[nodeId]
    if (!children) {
      return Object.values(selectedNodeMap).some(
        (node) =>
          String(node.parent_id ?? "") === nodeId &&
          !!node.deleted_at &&
          selectedFileIds.has(String(node.id))
      )
    }

    const mergedChildren = mergeChildrenWithSelectedDeleted(nodeId, children)
    for (const child of mergedChildren) {
      if (child.node_type === "FOLDER") {
        if (hasDeletedSelectedInSubtree(String(child.id))) return true
      } else if (child.deleted_at && selectedFileIds.has(String(child.id))) {
        return true
      }
    }

    return false
  }

  const fetchDescendantReadyFileIds = async (
    nodeId: string
  ): Promise<string[]> => {
    if (!organizationId || !workspaceId) return []
    const fileIds: string[] = []
    const pending = [nodeId]
    const visited = new Set<string>()

    while (pending.length > 0) {
      const currentId = pending.shift()
      if (!currentId || visited.has(currentId)) continue
      visited.add(currentId)

      const children =
        childrenByParentId[currentId] ??
        (
          await listChildNodes({
            urlBuilderParams: {
              organizationId,
              workspaceId,
            },
            searchParams: new URLSearchParams({
              parent_id: currentId,
            }),
          })
        )?.data ??
        []

      setChildrenByParentId((prev) => ({
        ...prev,
        [currentId]: children,
      }))

      for (const child of children) {
        if (child.node_type === "FOLDER") {
          pending.push(String(child.id))
        } else if (isSelectableFile(child)) {
          fileIds.push(String(child.id))
        }
      }
    }

    return fileIds
  }

  const deletedNodeIds = useMemo(
    () => selectedNodeIds.filter((id) => !!selectedNodeMap[id]?.deleted_at),
    [selectedNodeIds, selectedNodeMap]
  )
  const missingNodeIds = useMemo(
    () => selectedNodeIds.filter((id) => missingSelectedIds.has(id)),
    [selectedNodeIds, missingSelectedIds]
  )
  const failedNodeIds = useMemo(
    () =>
      selectedNodeIds.filter((id) => selectedNodeMap[id]?.status === "FAILED"),
    [selectedNodeIds, selectedNodeMap]
  )
  const hasInvalidSelections = useMemo(
    () =>
      deletedNodeIds.length > 0 ||
      missingNodeIds.length > 0 ||
      failedNodeIds.length > 0,
    [deletedNodeIds, failedNodeIds, missingNodeIds]
  )
  const invalidSelectedIds = useMemo(
    () =>
      Array.from(
        new Set([...deletedNodeIds, ...failedNodeIds, ...missingNodeIds])
      ),
    [deletedNodeIds, failedNodeIds, missingNodeIds]
  )
  const selectedNodesResolveErrorMessage = selectedNodesResolveFailed
    ? getUserFacingApiErrorMessage(
        selectedNodesResolveError,
        t,
        t(
          "workspace.agent.basicForm.unifiedToolSelector.resolveKnowledgeNodesFailed"
        )
      )
    : null

  useEffect(() => {
    onInvalidSelectionChange?.(tool.id, hasInvalidSelections)
  }, [hasInvalidSelections, onInvalidSelectionChange, tool.id])

  const { selectedAncestorIds, deletedAncestorIds } = useMemo(() => {
    const selectedSet = new Set<string>()
    const deletedSet = new Set<string>()

    const walkUp = (nodeId: string, markDeleted: boolean) => {
      let currentId = nodeId
      const seen = new Set<string>()
      while (currentId && !seen.has(currentId)) {
        seen.add(currentId)
        const node = selectedNodeMap[currentId]
        if (!node || !node.parent_id) break
        const parentId = String(node.parent_id)
        selectedSet.add(parentId)
        if (markDeleted) deletedSet.add(parentId)
        currentId = parentId
      }
    }

    selectedNodeIds.forEach((id) => {
      const isDeleted = !!selectedNodeMap[id]?.deleted_at
      walkUp(id, isDeleted)
    })

    return { selectedAncestorIds: selectedSet, deletedAncestorIds: deletedSet }
  }, [selectedNodeIds, selectedNodeMap])

  const rootNodes = useMemo(() => {
    const baseRoots = (rootsResp?.data ?? []).filter(
      (node) => node.node_type === "FOLDER"
    )
    const deletedSelectedRoots = Object.values(selectedNodeMap).filter(
      (node) =>
        node.node_type === "FOLDER" &&
        !!node.deleted_at &&
        !node.parent_id &&
        deletedAncestorIds.has(String(node.id))
    )
    if (deletedSelectedRoots.length === 0) return baseRoots
    const mergedMap = new Map<string, KnowledgeNodeSchema>()
    baseRoots.forEach((node) => mergedMap.set(String(node.id), node))
    deletedSelectedRoots.forEach((node) => mergedMap.set(String(node.id), node))
    return Array.from(mergedMap.values())
  }, [deletedAncestorIds, rootsResp?.data, selectedNodeMap])

  const deletedByAncestor = useMemo(() => {
    const map = new Map<string, Set<string>>()
    const deletedIds = selectedNodeIds.filter(
      (id) => !!selectedNodeMap[id]?.deleted_at
    )

    const addToAncestor = (ancestorId: string, deletedId: string) => {
      if (!map.has(ancestorId)) {
        map.set(ancestorId, new Set())
      }
      map.get(ancestorId)?.add(deletedId)
    }

    deletedIds.forEach((deletedId) => {
      let currentId = deletedId
      const seen = new Set<string>()
      while (currentId && !seen.has(currentId)) {
        seen.add(currentId)
        const node = selectedNodeMap[currentId]
        if (!node || !node.parent_id) break
        const parentId = String(node.parent_id)
        addToAncestor(parentId, deletedId)
        currentId = parentId
      }
    })

    return map
  }, [selectedNodeIds, selectedNodeMap])

  const updateFolderSelection = async (folderId: string, checked: boolean) => {
    if (disabled) return
    setSelectingFolderIds((prev) => {
      const next = new Set(prev)
      next.add(folderId)
      return next
    })
    try {
      const fileIds = await fetchDescendantReadyFileIds(folderId)
      const deletedIds = Array.from(deletedByAncestor.get(folderId) ?? [])
      updateKnowledgeNodeSelection((currentIds) =>
        checked
          ? [...currentIds, ...fileIds, ...deletedIds]
          : currentIds.filter(
              (id) => !fileIds.includes(id) && !deletedIds.includes(id)
            )
      )
    } catch (error) {
      console.error("Failed to update knowledge folder selection", error)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t(
            "workspace.agent.basicForm.unifiedToolSelector.updateKnowledgeFolderSelectionFailed"
          ),
          fallbackDescription: t(
            "workspace.agent.basicForm.unifiedToolSelector.updateKnowledgeFolderSelectionFailedDescription"
          ),
        }),
      })
    } finally {
      setSelectingFolderIds((prev) => {
        const next = new Set(prev)
        next.delete(folderId)
        return next
      })
    }
  }

  const renderFolderCheckbox = (
    folderId: string,
    descendantIds: Set<string>,
    onToggle: (checked: boolean) => void
  ) => {
    const descendantArray = Array.from(descendantIds)
    const hasAnySelected =
      descendantArray.some((id) => selectedFileIds.has(id)) ||
      selectedAncestorIds.has(folderId)
    const allSelected =
      descendantArray.length > 0 &&
      descendantArray.every((id) => selectedFileIds.has(id))
    const indeterminate = hasAnySelected && !allSelected

    return (
      <input
        type="checkbox"
        checked={allSelected}
        ref={(el) => {
          if (el) {
            el.indeterminate = indeterminate
          }
        }}
        onChange={(e) => onToggle(e.target.checked)}
        disabled={disabled || selectingFolderIds.has(folderId)}
      />
    )
  }

  const renderKnowledgeNodesLoadError = (error: unknown) => (
    <div
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
    >
      {getUserFacingApiErrorMessage(
        error,
        t,
        t(
          "workspace.agent.basicForm.unifiedToolSelector.loadKnowledgeNodesFailed"
        )
      )}
    </div>
  )

  const renderNodeChildren = (
    parentId: string,
    depth: number
  ): React.ReactNode => {
    const loadError = childrenLoadErrors[parentId]
    if (loadError) {
      return renderKnowledgeNodesLoadError(loadError)
    }
    const children = childrenByParentId[parentId]
    if (children === undefined) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("common.loading")}
        </div>
      )
    }

    const mergedChildren = mergeChildrenWithSelectedDeleted(parentId, children)
    const visibleChildren = getVisibleChildren(mergedChildren)
    if (visibleChildren.length === 0) {
      return (
        <div className="text-xs text-muted-foreground">
          {t("common.contextSourceDropdown.noObjects")}
        </div>
      )
    }

    return (
      <div className="space-y-1">
        {visibleChildren.map((node) => {
          const nodeId = String(node.id)
          const toggleFileSelection = (forceChecked?: boolean) => {
            if (disabled) return
            const isChecked = selectedFileIds.has(nodeId)
            if (!isChecked && !isSelectableFile(node)) return
            const nextChecked = forceChecked ?? !isChecked
            updateKnowledgeNodeSelection((currentIds) =>
              nextChecked
                ? [...currentIds, nodeId]
                : currentIds.filter((id) => id !== nodeId)
            )
          }
          if (node.node_type === "FOLDER") {
            const descendantIds = new Set<string>()
            collectDescendantFileIdsFromCache(nodeId, descendantIds)
            const isDeleted = !!node.deleted_at

            return (
              <div key={nodeId} className="space-y-1">
                <div
                  className="flex items-center gap-2"
                  style={{ paddingLeft: depth * 12 }}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => toggleNodeExpanded(nodeId)}
                    disabled={disabled}
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        expandedNodeIds.has(nodeId) ? "rotate-180" : ""
                      }`}
                    />
                  </Button>
                  {renderFolderCheckbox(
                    nodeId,
                    descendantIds,
                    (checked) => void updateFolderSelection(nodeId, checked)
                  )}
                  <button
                    type="button"
                    className="flex items-center gap-2 text-left"
                    onClick={() => toggleNodeExpanded(nodeId)}
                    disabled={disabled}
                  >
                    <Folder
                      className={`h-4 w-4 ${
                        isDeleted
                          ? "text-gray-400 dark:text-gray-600"
                          : "text-muted-foreground"
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        isDeleted
                          ? "text-gray-400 line-through dark:text-gray-500"
                          : ""
                      }`}
                    >
                      {node.name}
                    </span>
                  </button>
                  {hasDeletedSelectedInSubtree(nodeId) && (
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  )}
                </div>
                {expandedNodeIds.has(nodeId) && (
                  <div className="space-y-1">
                    {renderNodeChildren(nodeId, depth + 1)}
                  </div>
                )}
              </div>
            )
          }

          const checked = selectedFileIds.has(nodeId)
          const isDeleted = !!node.deleted_at
          const isUnavailable = isDeleted || node.status === "FAILED"
          return (
            <div
              key={nodeId}
              className="flex items-start gap-2"
              style={{ paddingLeft: depth * 12 }}
              onClick={() => toggleFileSelection()}
            >
              <span className="h-6 w-6 flex-none" />
              <input
                type="checkbox"
                checked={checked}
                className="mt-0.5 flex-none"
                onChange={(e) => {
                  toggleFileSelection(e.target.checked)
                }}
                onClick={(e) => e.stopPropagation()}
                disabled={disabled || (!checked && !isSelectableFile(node))}
              />
              <div className="flex min-w-0 flex-1 items-start gap-2">
                <FileText
                  className={`mt-0.5 h-4 w-4 flex-none ${
                    isUnavailable
                      ? "text-gray-400 dark:text-gray-600"
                      : "text-muted-foreground"
                  }`}
                />
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left text-sm leading-5"
                  onClick={() => toggleFileSelection()}
                  disabled={disabled}
                >
                  <span
                    className={`break-words ${
                      isUnavailable
                        ? "text-gray-400 line-through dark:text-gray-500"
                        : ""
                    }`}
                  >
                    {node.name}
                  </span>
                </button>
              </div>
              {isUnavailable && (
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-yellow-500" />
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {hasInvalidSelections && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-100">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
            <div className="space-y-2">
              <div>
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.invalidKnowledgeNodesWarning"
                )}
              </div>
              <div className="space-y-1">
                {invalidSelectedIds.map((nodeId) => (
                  <div
                    key={nodeId}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="min-w-0 truncate">
                      {selectedNodeMap[nodeId]?.name ||
                        t(
                          "workspace.agent.basicForm.unifiedToolSelector.unavailableKnowledgeNode"
                        )}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={disabled}
                      onClick={() =>
                        updateKnowledgeNodeSelection((currentIds) =>
                          currentIds.filter((id) => id !== nodeId)
                        )
                      }
                    >
                      {t(
                        "workspace.agent.basicForm.unifiedToolSelector.removeKnowledgeNode"
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedNodesResolveFailed && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-100">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
            <div>{selectedNodesResolveErrorMessage}</div>
          </div>
        </div>
      )}
      {showInvocationInstruction && (
        <InvocationInstructionField
          id={`invocation-${tool.id}`}
          placeholder={t(
            "workspace.agent.basicForm.unifiedToolSelector.knowledgeBaseInvocationInstructionPlaceholder"
          )}
          value={tool.invocationInstruction}
          onChange={(value) =>
            onUpdateTool(tool.id, {
              invocationInstruction: value,
            })
          }
          disabled={disabled}
        />
      )}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          {t("common.contextObjectSelectDialog.title")}
        </Label>
        <div className="text-xs text-muted-foreground">
          {t("common.contextObjectSelectDialog.description")}
        </div>
        <div className="border rounded-md p-2 max-h-[320px] overflow-auto">
          {rootsStatus === FetchStatus.Loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("common.loading")}
            </div>
          )}
          {rootsStatus !== FetchStatus.Loading &&
            rootsError != null &&
            renderKnowledgeNodesLoadError(rootsError)}
          {rootsStatus !== FetchStatus.Loading && rootsError == null && (
            <div className="space-y-1">
              {rootNodes.length === 0 && (
                <div className="text-xs text-muted-foreground">
                  {t("common.contextSourceDropdown.noObjects")}
                </div>
              )}
              {rootNodes.map((root) => {
                const descendantIds = new Set<string>()
                collectDescendantFileIdsFromCache(
                  String(root.id),
                  descendantIds
                )
                const isDeleted = !!root.deleted_at
                return (
                  <div key={root.id} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => toggleNodeExpanded(String(root.id))}
                        disabled={disabled}
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            expandedNodeIds.has(String(root.id))
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </Button>
                      {renderFolderCheckbox(
                        String(root.id),
                        descendantIds,
                        (checked) =>
                          void updateFolderSelection(String(root.id), checked)
                      )}
                      <button
                        type="button"
                        className="flex items-center gap-2 text-left"
                        onClick={() => toggleNodeExpanded(String(root.id))}
                        disabled={disabled}
                      >
                        <Folder
                          className={`h-4 w-4 ${
                            isDeleted
                              ? "text-gray-400 dark:text-gray-600"
                              : "text-muted-foreground"
                          }`}
                        />
                        <span
                          className={`text-sm ${
                            isDeleted
                              ? "text-gray-400 line-through dark:text-gray-500"
                              : ""
                          }`}
                        >
                          {root.name}
                        </span>
                      </button>
                      {hasDeletedSelectedInSubtree(String(root.id)) && (
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      )}
                    </div>
                    {expandedNodeIds.has(String(root.id)) && (
                      <div className="pl-6 space-y-1">
                        {renderNodeChildren(String(root.id), 1)}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
